import { useCallback, useEffect, useMemo, useState } from 'react'

// Shown (cycled) while Arc reviews the feedback — it looks at the screenshot and
// searches the code, which can take several seconds, so keep the user engaged.
const ARC_LOADING_MESSAGES = [
  'Reading your feedback…',
  'Looking at your screenshot…',
  'Searching the code…',
  'Pinpointing the right spot…',
  'Thinking of good questions…',
]
const ARC_LOADING_INTERVAL_MS = 2200

// Holds the Arc follow-up Q&A state: the generated questions, the user's
// answers, and the cycling loading messages shown while Arc is reviewing.
const useArcFollowUps = () => {
  const [followUpQuestions, setFollowUpQuestions] = useState<string[]>([])
  const [followUpAnswers, setFollowUpAnswers] = useState<string[]>([])
  const [isGeneratingFollowUps, setIsGeneratingFollowUps] = useState(false)
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)

  // Cycle the engaging loading messages while Arc reviews the feedback.
  useEffect(() => {
    if (!isGeneratingFollowUps) {
      setLoadingMessageIndex(0)
      return
    }
    const timer = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % ARC_LOADING_MESSAGES.length)
    }, ARC_LOADING_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [isGeneratingFollowUps])

  const startFollowUpQuestions = useCallback((questions: string[]) => {
    setFollowUpQuestions(questions)
    setFollowUpAnswers(questions.map(() => ''))
  }, [])

  const handleAnswerChange = useCallback((index: number, value: string) => {
    setFollowUpAnswers((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }, [])

  const transcript = useMemo(
    () =>
      followUpQuestions.map((question, i) => ({
        question,
        answer: followUpAnswers[i]?.trim() || '',
      })),
    [followUpQuestions, followUpAnswers],
  )

  const resetFollowUps = useCallback(() => {
    setFollowUpQuestions([])
    setFollowUpAnswers([])
    setIsGeneratingFollowUps(false)
  }, [])

  return {
    followUpQuestions,
    followUpAnswers,
    isGeneratingFollowUps,
    setIsGeneratingFollowUps,
    loadingMessageIndex,
    loadingMessage: ARC_LOADING_MESSAGES[loadingMessageIndex],
    transcript,
    startFollowUpQuestions,
    handleAnswerChange,
    resetFollowUps,
  }
}

export default useArcFollowUps
